import React from "react"
import { observer } from "mobx-react-lite"
import { FieldProps } from "formik"
import without from "lodash/without"
import debounce from "lodash/debounce"

import Dropdown from "@components/prototypes/FilterSidebar/components/Dropdown/Dropdown"
import NotFound from "@components/ui/NotFound/NotFound"
import Loader from "@components/ui/Loader/BarLoader"
import Templates from "@components/ui/Templates"
import { useStore } from "@store/index"
import CheckboxWithLabel from "@components/ui/Checkbox/CheckboxWithLabel"
import ListItem from "@components/ui/ListItem/ListItem"
import { DataCategoryEntity } from "@framework/types/knowledge"
import DocumentIcon from "@components/ui/Icon/DocumentIcon"
import { IconName } from "@components/ui/Icon/IconName"
import List from "@components/ui/List/List"
import InfiniteCollection from "@components/modals/EditModal/components/BaseContainer/InfiniteCollection"
import { SearchContext } from "@components/prototypes/SearchContext"

import styles from "./DataTypeSelect.module.sass"

const DEBOUNCE_TIMEOUT = 500

export interface DataTypeSelectProps extends FieldProps<string[]> {
  label?: string
  valueKey?: keyof DataCategoryEntity
}

const DataTypeSelect: React.FC<DataTypeSelectProps> = observer(
  ({ label, field, form, valueKey = "id" }) => {
    const context = React.useContext(SearchContext)

    const [open, setOpen] = React.useState(false)

    const { value = [], name } = field
    const { setFieldValue } = form

    const { allDatatypesStore } = useStore()

    const handleClick = (newValue: string) => {
      if (!value.includes(newValue)) {
        setFieldValue(name, [...value, newValue])
      } else {
        setFieldValue(name, without(value, newValue))
      }
    }

    const debouncedSearch = React.useCallback(
      debounce(allDatatypesStore.search, DEBOUNCE_TIMEOUT),
      []
    )

    React.useEffect(() => {
      debouncedSearch(context?.query ?? "")
    }, [context?.query])

    const renderRow = (dataType: DataCategoryEntity) => {
      const active = value?.includes(dataType[valueKey] as string)

      return (
        <ListItem
          active={active}
          onClick={() => handleClick(dataType[valueKey] as string)}
        >
          <List direction="row" gutter="8" align="center">
            <DocumentIcon
              icon={(dataType.iconUrl as IconName) ?? "global"}
              size="medium"
            />
            {dataType.name}
          </List>
        </ListItem>
      )
    }

    const isChecked = !!value?.length

    const handleUncheck = (e: React.MouseEvent) => {
      if (isChecked) setFieldValue(name, undefined)
      else setOpen(true)
      e.stopPropagation()
    }

    return (
      <Dropdown
        title={label}
        opened={open}
        onClick={() => setOpen((value) => !value)}
        before={
          <CheckboxWithLabel
            checked={!!value?.length}
            onClick={handleUncheck}
          />
        }
      >
        <Templates.RollScript className={styles.root}>
          <div className={styles.container}>
            {allDatatypesStore.state.total === 0 ? (
              allDatatypesStore.state.isLoading ? (
                <Loader size="large" fluid />
              ) : (
                <NotFound>No Content Types found</NotFound>
              )
            ) : (
              <InfiniteCollection<DataCategoryEntity>
                total={allDatatypesStore.state.total}
                getItem={allDatatypesStore.getByIndex}
                renderItem={renderRow}
                minRowHeight={42}
              />
            )}
          </div>
        </Templates.RollScript>
      </Dropdown>
    )
  }
)

export default DataTypeSelect
